import Phaser from 'phaser';
import { UI_CENTER_X } from '../core/Config';
import { DebugLog } from '../core/DebugLog';
import { Player } from '../engine/Player';
import { EditorDraftStore } from '../devtools/EditorDraftStore';

const MODES = ['collision', 'interactable', 'npc_zone', 'occluder'] as const;
type DevMode = (typeof MODES)[number];
type OverlayContext = { worldKey: string; worldType: 'village' | 'house' };
const SNAP_STEPS = [4, 8, 16, 32, 64];

const MODE_LABELS: Record<DevMode, string> = {
  collision: '충돌',
  interactable: '상호작용',
  npc_zone: 'NPC 구역',
  occluder: '가림 오브젝트',
};

const WORLD_TYPE_LABELS: Record<OverlayContext['worldType'], string> = {
  village: '마을',
  house: '집',
};

export class DeveloperOverlay {
  private readonly text: Phaser.GameObjects.Text;
  private readonly dragRect: Phaser.GameObjects.Rectangle;
  private readonly toast: Phaser.GameObjects.Text;
  private readonly gridGraphics: Phaser.GameObjects.Graphics;
  private enabled = false;
  private dragStart?: Phaser.Math.Vector2;
  private mode: DevMode = 'collision';
  private lastPayload?: unknown;
  private snapIndex = 2;
  private gridVisible = true;

  constructor(private readonly scene: Phaser.Scene, private readonly context: OverlayContext) {
    this.text = scene.add
      .text(20, 20, '', {
        fontSize: '15px',
        color: '#dbeafe',
        backgroundColor: '#07101add',
        padding: { x: 12, y: 10 },
        lineSpacing: 6,
        fixedWidth: 420,
      })
      .setDepth(10030)
      .setScrollFactor(0)
      .setVisible(false);

    this.gridGraphics = scene.add.graphics().setDepth(10029).setVisible(false);
    this.dragRect = scene.add
      .rectangle(0, 0, 0, 0)
      .setOrigin(0)
      .setStrokeStyle(2, 0xfb7185)
      .setDepth(10031)
      .setScrollFactor(1)
      .setVisible(false);
    this.toast = scene.add
      .text(UI_CENTER_X, 92, '', {
        fontSize: '18px',
        color: '#fef3c7',
        backgroundColor: '#111827dd',
        padding: { x: 10, y: 6 },
      })
      .setOrigin(0.5, 0)
      .setDepth(10032)
      .setScrollFactor(0)
      .setVisible(false);

    scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.enabled) return;
      const snapped = this.snapPoint(pointer.worldX, pointer.worldY);
      this.dragStart = new Phaser.Math.Vector2(snapped.x, snapped.y);
      this.dragRect.setPosition(snapped.x, snapped.y).setSize(0, 0).setVisible(true);
    });

    scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.enabled || !this.dragStart || !pointer.isDown) return;
      const snapped = this.snapPoint(pointer.worldX, pointer.worldY);
      const x = Math.min(this.dragStart.x, snapped.x);
      const y = Math.min(this.dragStart.y, snapped.y);
      const w = Math.abs(snapped.x - this.dragStart.x);
      const h = Math.abs(snapped.y - this.dragStart.y);
      this.dragRect.setPosition(x, y).setSize(w, h);
    });

    scene.input.on('pointerup', async (pointer: Phaser.Input.Pointer) => {
      if (!this.enabled || !this.dragStart) return;
      const snapped = this.snapPoint(pointer.worldX, pointer.worldY);
      const payload = this.buildPayload(snapped.x, snapped.y);
      this.lastPayload = payload;
      console.log(payload);
      try {
        await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
        DebugLog.info('DeveloperOverlay', 'payload copied', { mode: this.mode });
        this.showToast(`${this.getModeLabel()} JSON을 복사했습니다.`);
      } catch {
        DebugLog.warn('DeveloperOverlay', 'payload copy failed; dumped to console', { mode: this.mode });
        this.showToast(`${this.getModeLabel()} JSON을 콘솔에 출력했습니다.`);
      }
      this.dragStart = undefined;
      this.dragRect.setVisible(false);
    });

    scene.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (!this.enabled) return;
      if (event.key === '1') this.setMode('collision');
      if (event.key === '2') this.setMode('interactable');
      if (event.key === '3') this.setMode('npc_zone');
      if (event.key === '4') this.setMode('occluder');
      if (event.key === '5') this.saveLastPayload();
      if (event.key === '6') this.downloadWorldDraft();
      if (event.key === '7') this.copyLastPayload();
      if (event.key === '8') this.copyFullDraft();
      if (event.key === '0') this.clearWorldDraft();
      if (event.key.toLowerCase() === 'g') this.toggleGrid();
      if (event.key === '[') this.changeSnap(-1);
      if (event.key === ']') this.changeSnap(1);
    });
  }

  toggle() {
    this.enabled = !this.enabled;
    this.text.setVisible(this.enabled);
    this.gridGraphics.setVisible(this.enabled && this.gridVisible);
    if (!this.enabled) {
      this.dragRect.setVisible(false);
      this.toast.setVisible(false);
    }
    this.drawGrid();
  }

  isEnabled() {
    return this.enabled;
  }

  toggleGrid() {
    this.gridVisible = !this.gridVisible;
    this.gridGraphics.setVisible(this.enabled && this.gridVisible);
    this.drawGrid();
    this.showToast(`그리드 ${this.gridVisible ? 'ON' : 'OFF'}`);
  }

  changeSnap(delta: number) {
    this.snapIndex = Phaser.Math.Clamp(this.snapIndex + delta, 0, SNAP_STEPS.length - 1);
    this.drawGrid();
    this.showToast(`스냅 ${this.getSnap()}px`);
  }

  update(player: Player) {
    if (!this.enabled) return;
    const pointer = this.scene.input.activePointer;
    const snapped = this.snapPoint(pointer.worldX, pointer.worldY);
    const counts = EditorDraftStore.getCounts(this.context.worldKey);
    const recentLogs = DebugLog.recent(4).map((entry) => {
      const hhmmss = entry.timestamp.slice(11, 19);
      return `${hhmmss} ${entry.scope} ${entry.message}`;
    });

    this.text.setText([
      '개발자 모드',
      '',
      `월드        ${this.context.worldKey} (${WORLD_TYPE_LABELS[this.context.worldType]})`,
      `모드        ${this.getModeLabel()}   [1~4]`,
      `그리드      ${this.gridVisible ? 'ON' : 'OFF'}   [G]`,
      `스냅        ${this.getSnap()}px   [[ ]]`,
      `초안 개수   c${counts.collision} / i${counts.interactable} / n${counts.npc_zone} / o${counts.occluder}`,
      '',
      '저장        5 저장 / 6 다운로드',
      '복사        7 payload / 8 전체',
      '초기화      0 현재 월드',
      '',
      `마우스      ${snapped.x}, ${snapped.y}`,
      `플레이어    ${Math.round(player.x)}, ${Math.round(player.y)}`,
      '',
      '최근 로그',
      ...recentLogs,
    ]);
    this.drawGrid();
  }

  getPanelState() {
    return {
      visible: this.enabled,
      worldKey: this.context.worldKey,
      worldType: this.context.worldType,
      mode: this.getModeLabel(),
      snap: this.getSnap(),
      gridVisible: this.gridVisible,
      draftCounts: EditorDraftStore.getCounts(this.context.worldKey),
      lastPayload: this.lastPayload ? JSON.stringify(this.lastPayload) : undefined,
      recentLogs: DebugLog.recent(6).map((entry) => {
        const hhmmss = entry.timestamp.slice(11, 19);
        return `${hhmmss} ${entry.scope} ${entry.message}`;
      }),
    };
  }

  private getSnap() {
    return SNAP_STEPS[this.snapIndex];
  }

  private getModeLabel() {
    return MODE_LABELS[this.mode];
  }

  private snapPoint(x: number, y: number) {
    const snap = this.getSnap();
    return { x: Math.round(x / snap) * snap, y: Math.round(y / snap) * snap };
  }

  private setMode(mode: DevMode) {
    this.mode = mode;
    this.showToast(`모드를 ${this.getModeLabel()}로 바꿨습니다.`);
  }

  private buildPayload(worldX: number, worldY: number) {
    const x = Math.round(Math.min(this.dragStart!.x, worldX));
    const y = Math.round(Math.min(this.dragStart!.y, worldY));
    const w = Math.round(Math.abs(worldX - this.dragStart!.x));
    const h = Math.round(Math.abs(worldY - this.dragStart!.y));

    if (this.mode === 'occluder') {
      return { id: 'new_occluder', x, y: y + h, spriteKey: 'tree_base', frontSpriteKey: 'tree_front' };
    }
    if (this.mode === 'interactable') {
      return { id: 'new_interaction', type: 'house_entry', houseId: 's01_001', zone: { x, y, w, h } };
    }
    return { x, y, w, h };
  }

  private saveLastPayload() {
    if (!this.lastPayload) {
      this.showToast('먼저 드래그해서 payload를 만드세요.');
      return;
    }
    EditorDraftStore.append(this.context.worldKey, this.mode, this.lastPayload);
    this.showToast(`${this.getModeLabel()} 1개를 초안에 저장했습니다.`);
  }

  private downloadWorldDraft() {
    const json = EditorDraftStore.exportWorldJson(this.context.worldKey);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.context.worldKey}.draft.json`;
    a.click();
    URL.revokeObjectURL(url);
    this.showToast('초안 파일을 내려받았습니다.');
  }

  private async copyLastPayload() {
    if (!this.lastPayload) {
      this.showToast('복사할 payload가 없습니다.');
      return;
    }
    await this.copyText(JSON.stringify(this.lastPayload, null, 2), 'payload를 복사했습니다.');
  }

  private async copyFullDraft() {
    await this.copyText(EditorDraftStore.exportWorldJson(this.context.worldKey), '전체 초안을 복사했습니다.');
  }

  private clearWorldDraft() {
    EditorDraftStore.clearWorld(this.context.worldKey);
    this.showToast('현재 월드 초안을 초기화했습니다.');
  }

  private drawGrid() {
    this.gridGraphics.clear();
    if (!this.enabled || !this.gridVisible) return;
    const camera = this.scene.cameras.main;
    const snap = this.getSnap();
    const startX = Math.floor(camera.worldView.x / snap) * snap;
    const endX = Math.ceil((camera.worldView.x + camera.worldView.width) / snap) * snap;
    const startY = Math.floor(camera.worldView.y / snap) * snap;
    const endY = Math.ceil((camera.worldView.y + camera.worldView.height) / snap) * snap;
    this.gridGraphics.lineStyle(1, 0x38bdf8, 0.18);
    for (let x = startX; x <= endX; x += snap) this.gridGraphics.lineBetween(x, startY, x, endY);
    for (let y = startY; y <= endY; y += snap) this.gridGraphics.lineBetween(startX, y, endX, y);
  }

  private showToast(message: string) {
    this.toast.setText(message).setVisible(true);
    this.scene.time.delayedCall(1600, () => this.toast.setVisible(false));
  }

  private async copyText(text: string, successMessage: string) {
    try {
      await navigator.clipboard.writeText(text);
      this.showToast(successMessage);
    } catch {
      console.log(text);
      this.showToast('복사에 실패해 콘솔에 출력했습니다.');
    }
  }
}
